import { CartDiscount, DiscountType } from "@/interface/Cart"
import { OrderProduct } from "@/interface/orders/OrderProduct"
import { useCustomerReceiptStore } from "@/store/CustomerReceiptConfirmation"
import { multiLocationStore } from "@/store/MultiLocation"
import { useUserStore } from "@/store/User"
import { paramsSerializer } from "@/utils/api/paramsSerializer"
import { defineStore } from "pinia"
import { Order } from "@/interface/orders/Order"
import axios, { AxiosResponse } from "axios"
import {
    DataFormatter,
    defaultFormatter,
} from "@/pages/orders/utils/useOrdersSorting"
import { useSettingsStore } from "@/store/Settings"
import { printOrder } from "@/services/printer/PrinterService"
import { useMasterSlave } from "@/utils/useMasterSlave"
import { DateTime } from "luxon"
import { cloneDeep } from "lodash"
import { getDefaultCustomer } from "@/interface/Customers/Customer"
import { CartState, useCartStore } from "@/store/cart/Cart"
import { PaymentMethod } from "@/pages/pos/ticket/PaymentMethod"
import { setToastNotification } from "@/ui-elements/toast-notification/setToastNotification"
import i18n from "@/i18n"
import { isInStore } from "@/pages/orders/order/utils/isInStore"
import { useWorkingDay } from "@/utils/useWorkingDay"
import { usePOSStore } from "@/store/POS"
import { UnwrapRef } from "vue"
import { kitchenScreenStore } from "@/store/KitchenScreen"

const ordersScreenExceptionSources = ["online", "counter"]
const numberBuddySources = ["online", "counter", "kiosk"]

// @ts-ignore
export const useOrdersStore = defineStore({
    id: "orders",
    state: () =>
        ({
            orders: [],
            kitchenOrders: [],
            futureOrderCount: 0,
            loading: false,
            dataReady: false,
            isShowingOrderId: null,
            showTimeImmediately: false,
            playSoundOnce: false,
            playSound: false,
            autoOpened: false,
            newOrderIds: [],
            show: {
                internal: false,
                external: false,
            },
            updatedAt: DateTime.now().toUnixInteger(),
            kitchenOrdersLoaded: false,
            cleanupInterval: null,
        } as {
            orders: Order[]
            kitchenOrders: Order[]
            futureOrderCount: number
            playSoundOnce: boolean
            playSound: boolean
            loading: boolean
            dataReady: boolean
            isShowingOrderId: number | null
            showTimeImmediately: boolean
            autoOpened: boolean
            newOrderIds: number[]
            show: {
                internal: boolean
                external: boolean
            }
            updatedAt: number
            kitchenOrdersLoaded: boolean
            cleanupInterval: number | null
        }),
    getters: {
        orderData:
            (state) =>
            (orderId: number): Order | undefined => {
                return state.orders.find(
                    (order: Order): boolean => order.id == orderId
                )
            },
        isWaiterBuddy:
            () =>
            (order: Order): boolean =>
                order?.source?.type === "counter" &&
                (order.source.info?.match("ios|android") ?? []).length > 0,
        isQrTable:
            () =>
            (order: Order): boolean =>
                order?.source?.type === "online" &&
                order.source.info === "qr_table",
        ordersScreenOrders: (state) => {
            // All orders should be shown if one of these settings is true
            const showAllOrders: number =
                Number(useSettingsStore().settings.ob_show_always) ||
                Number(useSettingsStore().settings.numberbuddy_show_pos_orders)

            return state.orders.filter((order): boolean => {
                // Because of the new way we handle data hydration, we need to check this in the frontend too.
                // Because (created) events for future orders are also broadcasted on the websocket.
                const workingDay = useWorkingDay()
                if (
                    !(
                        DateTime.fromJSDate(new Date(order.created_at), {
                            zone: useSettingsStore().getTimeZone,
                        }) >= workingDay.startOfWorkingDay.value &&
                        DateTime.fromJSDate(new Date(order.created_at), {
                            zone: useSettingsStore().getTimeZone,
                        }) <= workingDay.endOfWorkingDay.value
                    )
                ) {
                    return false
                }

                if (order.payment.status !== "paid") {
                    return false
                }

                if (order.payment.result === "CREDIT") {
                    return false
                }

                if (showAllOrders) {
                    // We don’t want to show QR table orders, so we can't simply say 'show all'
                    // We still need to do some filtering
                    return (
                        !ordersScreenExceptionSources.includes(
                            order.source.type
                        ) || !!order.delivery
                    )
                } else {
                    if (order.table_id) {
                        return false
                    }
                }

                return (
                    !ordersScreenExceptionSources.includes(order.source.type) ||
                    (!!order.delivery && !!order.time_set)
                )
            })
        },
        filteredOrders(): Order[] {
            const orders = this.ordersScreenOrders

            // const filtered =
            return defaultFormatter(
                orders.filter((order): boolean => {
                    if (!this.show.internal && !this.show.external) {
                        return true
                    }
                    if (isInStore(order)) {
                        return this.show.internal
                    } else {
                        return this.show.external
                    }
                })
            )
        },
        numberBuddyOrders: (state): Order[] => {
            return state.orders.filter(
                (order: Order): boolean =>
                    numberBuddySources.includes(order.source.type) &&
                    order.delivery != "deliver"
            )
        },
        unseenOrders: (state): Order[] =>
            state.orders.length
                ? state.orders.filter(
                      (order: Order): boolean => !order.has_seen
                  )
                : [],
        fetchOrderUrl:
            () =>
            (
                orderId: number | null = null,
                orderFields: Array<string> = [],
                includes: Array<string> = [],
                customerFields: Array<string> = [],
                needMultiLocationAggregation: boolean = false,
                isForKitchen: boolean = false
            ) => {
                let url: string =
                    "client/orders" + (isForKitchen ? "/kitchen-screen" : "")
                if (orderId) {
                    url += `/${orderId}`
                }
                url += `?fields[orders]=${orderFields.join(",")}`

                if (includes.length) {
                    url += `&include=${includes.join(",")}`
                }

                if (customerFields.length) {
                    url += `&fields[customer]=${customerFields}`
                }

                if (needMultiLocationAggregation) {
                    url += `&${multiLocationStore().aggregateParameter()}`
                }

                return url
            },
    },
    actions: {
        _log(
            message: string,
            isError: boolean = false,
            stackTrace: any = null
        ) {
            if (window.ob.hasDebugModule("OrderStore")) {
                if (!isError) {
                    return console.log(`[OrderStore] ${message}`)
                }

                return console.error(`[OrderStore] ${message}`, stackTrace)
            }
        },
        getOrderLetter(orderId: Order["id"]) {
            const letters: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
            let index: number = this.orders.findIndex(
                (order: Order): boolean => order.id == orderId
            )
            let result: string = ""

            while (index >= 0) {
                result = letters[index % 26] + result
                index = Math.floor(index / 26) - 1
            }

            return result
        },
        getOrderLabel(order: Order): string {
            const label: number = order.refnr ?? order.nr ?? order.id

            // table name
            if (order.table_id && order.table_name) {
                return order.table_name
            }

            if (order.source.info === "qr") {
                return `QR Skip the line: ${label}`
            }

            if (order.source.type === "kiosk") {
                return `Kiosk: ${label}`
            }

            if (
                ["ue", "de"].includes(order.source.type) &&
                !order.address.street
            ) {
                return `#${order.source.id}`
            }

            if (
                (Boolean(Number(useSettingsStore().settings.ob_show_name)) ||
                    order.delivery === "pickup") &&
                order.address.first_name
            ) {
                return `${
                    order.address.company ? order.address.company + " " : ""
                }${order.address.first_name} ${order.address.last_name ?? ""}`
            }

            if (order.address.street) {
                return `${order.address.street} ${order.address.street_number} ${order.address.city}`
            }

            return `# ${label}`
        },
        async fetchOrders(
            dataFormatter: DataFormatter = defaultFormatter
        ): Promise<boolean> {
            const { t: translate } = i18n.global
            try {
                if (!this.dataReady) {
                    this.loading = true
                }

                let url: string = this.fetchOrderUrl(
                    null,
                    [
                        "id",
                        "client_id",
                        "nr",
                        "refnr",
                        "source",
                        "address",
                        "has_seen",
                        "is_v3",
                        "created_at",
                        "time",
                        "time_set",
                        "total",
                        "total_rounded",
                        "status",
                        "delivery",
                        "distance",
                        "duration",
                        "paid_at",
                        "time_start",
                        "time_set_start",
                    ],
                    [],
                    [],
                    true
                )

                if (this.dataReady && this.updatedAt) {
                    url += `&filter[updated_after]=${this.updatedAt}`
                }

                url += `&need_future_count`

                const response: AxiosResponse<any> = await axios.get(url)

                response.data.data.map((order: Order) => {
                    order.label = this.getOrderLabel(order)

                    return order
                })

                // This is the initial fetch. This is the only place where we 'mass' assign the orders to the state.
                if (!this.dataReady) {
                    this.orders = dataFormatter(response.data.data)
                    await this.loadOrders()
                    this.dataReady = true
                } else {
                    const apiOrders = response.data.data
                    const updatedOrders = apiOrders.filter(
                        (order: Order): boolean =>
                            this.orders.findIndex(
                                (existingOrder: UnwrapRef<Order>): boolean =>
                                    existingOrder.id == order.id
                            ) >= 0
                    )
                    const newOrders = apiOrders.filter(
                        (order: Order): boolean =>
                            this.orders.findIndex(
                                (existingOrder: UnwrapRef<Order>): boolean =>
                                    existingOrder.id == order.id
                            ) === -1
                    )

                    const updatedOrderIds = updatedOrders?.map(
                        (order: Order) => order.id
                    )
                    await this.loadOrders(updatedOrderIds)

                    this.orders = dataFormatter(
                        cloneDeep(this.orders).concat(newOrders)
                    )
                }

                this.futureOrderCount =
                    response.data?.meta?.future_orders_count || 0

                await this.checkNewOrders()

                this.updatedAt = DateTime.now().toUnixInteger()

                return true
            } catch (e) {
                console.error(e)
                setToastNotification(
                    translate("something_went_wrong"),
                    translate("orders_fetch_failed"),
                    "danger"
                )
                return false
            } finally {
                this.loading = false
            }
        },
        async fetchObject(objectId: number): Promise<Order> {
            const response: AxiosResponse<any> = await axios.get(
                `client/orders/${objectId}`,
                {
                    params: {
                        include: "products,table,deliverer",
                        main_location_id: multiLocationStore().isActive
                            ? multiLocationStore().main_location_id
                            : 0,
                    },
                    paramsSerializer: paramsSerializer,
                }
            )

            if (!response) {
                this._log(`cannot fetch order with id #${objectId}`)
                throw new Error("cannot fetch order!")
            }

            return response.data.data as Order
        },
        async objectPaid(objectId: number): Promise<boolean> {
            console.log("objectPaid: start")
            const order: Order = await this.fetchObject(objectId)

            // Print customer receipts, but only for counter orders that are coming from Waiterbuddy or QR table.
            // The kiosk already prints it and non-Waiterbuddy counter orders are printed in the direct flow.
            if (
                (order.source.type !== "counter" && !this.isQrTable(order)) ||
                (order.source.type === "counter" &&
                    !this.isWaiterBuddy(order)) ||
                !useMasterSlave().isMaster.value
            ) {
                console.log(
                    `objectPaid: early return because, you're not master, or it's counter and not Waiterbuddy, or it's not counter, source: ${order.source.type}`
                )

                return true
            }

            const shouldPrintCustomer: string = order.table_id
                ? useSettingsStore().settings.print_customer_table
                : useSettingsStore().settings.print_customer

            if (
                this.isWaiterBuddy(order) ||
                this.isQrTable(order) ||
                shouldPrintCustomer === "auto"
            ) {
                console.log("objectPaid: going to print Waiterbuddy or auto")
                await printOrder(order, {
                    printCustomerReceipts: true,
                    force: false,
                })
                return true
            } else if (shouldPrintCustomer === "ask") {
                console.log("objectPaid: going to print")
                useCustomerReceiptStore()
                    .askConfirmation()
                    .then(async (): Promise<void> => {
                        await printOrder(order, {
                            printCustomerReceipts: true,
                            force: true,
                        })
                    })
                    .catch(() => {})
            }

            return true
        },
        /** Only when an order is placed for the first time, the following function is called.
         *  We need to print the kitchen receipts. ***/
        async objectPlaced(objectId: number): Promise<boolean> {
            console.log("objectPlaced: start")
            try {
                // Sanity check: was the order not earlier received?
                const orderIndex: number = this.orders.findIndex(
                    (order: UnwrapRef<Order>): boolean => order.id == objectId
                )

                if (orderIndex > -1) {
                    console.log(
                        `objectPlaced: early return since order ${objectId} was already processed earlier`
                    )

                    return true
                }

                const order: Order = await this.fetchObject(objectId)
                const isKiosk: boolean = order.source.type === "kiosk"

                console.log(
                    `objectPlaced: isWaiterBuddy: ${this.isWaiterBuddy(
                        order
                    )}, isQrTable: ${this.isQrTable(order)}`
                )
                this._log(
                    `isWaiterBuddy: ${this.isWaiterBuddy(
                        order
                    )}, isQrTable: ${this.isQrTable(order)}`
                )

                // Perhaps the new order needs to be shown on the kitchen screen
                if (kitchenScreenStore().isActive) {
                    await kitchenScreenStore().placedOrder(order.id)
                }

                if (
                    !(
                        DateTime.fromJSDate(new Date(order.created_at), {
                            zone: useSettingsStore().getTimeZone,
                        }) >= useWorkingDay().startOfWorkingDay.value &&
                        DateTime.fromJSDate(new Date(order.created_at), {
                            zone: useSettingsStore().getTimeZone,
                        }) <= useWorkingDay().endOfWorkingDay.value
                    )
                ) {
                    console.log(
                        `objectPlaced: received an order that is not for the current working day. Order #${order.id} is not processed`
                    )
                    this._log(
                        `Received an order that is not for the current working day. Order #${order.id} is not processed`
                    )
                    return false
                }

                // We need to show the kiosk orders and orders with a time
                if (isKiosk || order.time_set) {
                    order.label = this.getOrderLabel(order)
                    order.is_complete = true

                    // Add new orders that to the front of the array
                    const saved: number = this.orders.unshift(order)

                    if (!saved) {
                        this._log("Order is not pushed to Order Store")
                        return false
                    }

                    this.orders = defaultFormatter(cloneDeep(this.orders))
                }

                if (
                    !useMasterSlave().isMaster.value ||
                    window.location.href.includes("/customer-screen") ||
                    useUserStore().waiterbuddy_platform
                ) {
                    console.log(
                        `objectPlaced: This screen is ${
                            !useMasterSlave().isMaster.value
                                ? "not master"
                                : window.location.href.includes(
                                      "/customer-screen"
                                  )
                                ? "a Customer screen"
                                : "Waiterbuddy"
                        }, so not printing order #${order.id}`
                    )
                    this._log(
                        `This screen is ${
                            !useMasterSlave().isMaster.value
                                ? "not master"
                                : window.location.href.includes(
                                      "/customer-screen"
                                  )
                                ? "a Customer screen"
                                : "Waiterbuddy"
                        }, so not printing order #${order.id}`
                    )
                    return true
                }

                /*** The rest of this function is about printing. If the order was seen, or it's a counter order,
                 **  that was not created in Waiterbuddy or QrTable, we don't print. The counter order was already
                 **  printed directly after saving the order ***/
                if (
                    order.has_seen ||
                    (order.source.type === "counter" &&
                        !this.isWaiterBuddy(order))
                ) {
                    console.log(
                        `objectPlaced: early return because is already seen, or it's counter and not Waiterbuddy (order seen: ${order.has_seen})`
                    )

                    return true
                }

                /*** Auto print and accept ***/
                if (
                    isKiosk ||
                    this.isWaiterBuddy(order) ||
                    this.isQrTable(order) ||
                    (useSettingsStore().settings.print_auto !== "0" &&
                        useSettingsStore().settings.print_when === "direct" &&
                        !order.has_seen)
                ) {
                    try {
                        this.playSoundOnce = true
                        console.log(
                            "objectPlaced going to print because kiosk, waiterbuddy or auto"
                        )
                        await printOrder(order)
                        await this.acceptOrder(objectId)

                        return true
                    } catch (e) {
                        this._log(
                            `Could not auto accept order #${objectId}`,
                            true,
                            e
                        )
                    }
                }

                /*** Show popup so the client can accept ***/
                if (!this.isShowingOrderId && order) {
                    this.playSound = true
                    await this.openOrderModal(
                        Number(objectId),
                        true,
                        useSettingsStore().settings.show_popup === "time"
                    )
                } else {
                    this.playSound = true
                }

                return true
            } catch (error: any) {
                this._log(error)

                return false
            }
        },
        async objectUpdated(objectId: number) {
            console.log("objectUpdated: start")
            const order: Order = await this.fetchObject(objectId)
            const existingObject: UnwrapRef<Order> | undefined =
                this.orders.find(
                    (order: Order): boolean => order.id == objectId
                )

            order.is_complete = true
            order.label = this.getOrderLabel(order)

            if (existingObject) {
                this.orders[this.orders.indexOf(existingObject)] = order
            }

            this.orders = defaultFormatter(cloneDeep(this.orders))

            // A new table order doesn't get the order placed event,
            // so we need to handle it here
            // @todo, perhaps nice to move this logic to the kitchen store
            if (kitchenScreenStore().isActive) {
                if (order.table_id) {
                    await kitchenScreenStore().placedOrder(order.id)
                } else {
                    await kitchenScreenStore().updateOrder(order.id)
                }
            }

            // For an ongoing table order, the object placed is not fired,
            // so we need to print the receipt here
            console.log(
                `objectUpdated: isWaiterBuddy? ${this.isWaiterBuddy(
                    order
                )}, isQrTable? ${this.isQrTable(order)}`
            )
            this._log(
                `isWaiterBuddy? ${this.isWaiterBuddy(
                    order
                )}, isQrTable? ${this.isQrTable(order)}`
            )

            if (
                (this.isWaiterBuddy(order) || this.isQrTable(order)) &&
                useMasterSlave().isMaster.value &&
                !order.has_seen
            ) {
                this._log(
                    `Printing WaiterBuddy or QrTable order #${order.id} (table: ${order.table_id})`
                )
                await this.acceptOrder(order.id)
                console.log(
                    `objectUpdated: Printing WaiterBuddy or QrTable order #${order.id} (table: ${order.table_id})`
                )
                await printOrder(order)
            } else {
                console.log(
                    `objectUpdated: Skipped printing because not master or other missing requirements! (has seen: ${order.has_seen})`
                )
                this._log(
                    `Skipped printing because not master or other missing requirements! (has seen: ${order.has_seen})`
                )
            }
        },
        objectDeleted(orderId: number) {
            this.orders = this.orders.filter(
                (order: Order): boolean => order.id != orderId
            )
        },
        updateOrder(orderId: number, data: Partial<Order>) {
            const existingOrder = this.orders.findIndex(
                (item: UnwrapRef<Order>): boolean => item.id == orderId
            )
            if (existingOrder >= 0) {
                this.orders[existingOrder] = Object.assign(
                    this.orders[existingOrder],
                    data
                )
            }
            this.orders = defaultFormatter(this.orders)
        },
        saveOrder(order: Order): void {
            const existingOrder: number = this.orders.findIndex(
                (item: UnwrapRef<Order>): boolean => item.id == order.id
            )
            if (existingOrder >= 0) {
                this.orders[existingOrder] = order
            } else {
                this.orders.push(order)
            }
            this.orders = defaultFormatter(this.orders)
        },
        async openOrderModal(
            orderId: number,
            autoOpened: boolean = false,
            showTimeImmediately: boolean = false
        ): Promise<void> {
            this.loading = true

            if (!this.orderData(orderId)?.is_complete) {
                await this.objectUpdated(orderId)
            }

            this.isShowingOrderId = orderId
            this.autoOpened = autoOpened
            this.showTimeImmediately = showTimeImmediately
            this.loading = false
        },
        async loadOrders(
            updatedOrderIds: Array<Number> | null = null
        ): Promise<void> {
            return this.orders.forEach((order: Order): void => {
                if (updatedOrderIds?.includes(order.id) || !order.is_complete) {
                    this.objectUpdated(order.id)
                }
            })
        },
        async checkNewOrders(): Promise<void> {
            const masterSlave = useMasterSlave()
            this._log("checkNewOrder: start")

            if (
                window.location.href.includes("/customer-screen") ||
                masterSlave.isSlave.value
            ) {
                return
            }

            const { settings } = useSettingsStore()

            if (Number(settings.hide_popup)) {
                this._log("checkNewOrder: hide_popup is set to 1")
                return
            }

            // Do we have a new order id?
            const newOrder: Order = this.unseenOrders[0]

            if (!newOrder) {
                this._log("checkNewOrder: no new order id")
                return
            }

            // We're already showing an order, skip for now
            if (this.isShowingOrderId) {
                this._log("checkNewOrder: isShowingOrderId is set")

                if (newOrder) {
                    this.playSound = true
                }

                return
            }

            if (
                (this.isWaiterBuddy(newOrder) || this.isQrTable(newOrder)) &&
                (newOrder?.delivery !== "deliver" || newOrder.table_id)
            ) {
                this._log(
                    "checkNewOrder: Skip waiterbuddy or qr table dinein order"
                )
                return
            }

            if (!newOrder.is_complete) {
                await this.objectUpdated(newOrder.id)
            }

            // Should always be true because we filter out orders that are already seen
            if (!newOrder.has_seen) {
                this._log("checkNewOrder: not seen", false, newOrder)
                if (
                    newOrder.source.type === "kiosk" ||
                    (settings.print_auto !== "0" &&
                        (settings.print_when === "direct" ||
                            newOrder.source.type === "counter"))
                ) {
                    try {
                        this.playSoundOnce = true
                        const order: AxiosResponse<any> = await axios.get(
                            "client/archive/" + newOrder?.id
                        )
                        this.saveOrder(order.data.data)
                        await printOrder(order.data.data).then(() =>
                            this._log(
                                "checkNewOrder: order has been sent to printer"
                            )
                        )
                        await this.acceptOrder(newOrder?.id).then(() =>
                            this._log("checkNewOrder: order has been accepted")
                        )
                    } catch (e) {
                        console.error(
                            "Could not auto accept order #" + newOrder?.id
                        )
                    }
                }

                await this.openOrderModal(
                    newOrder?.id,
                    true,
                    settings.show_popup === "time"
                )
            }
        },
        async acceptOrder(orderId: number): Promise<void> {
            this.updateOrder(orderId, { has_seen: true })
            await axios.post("/client/orders/seen/" + orderId)
        },
        closeOrderModal(): void {
            this.isShowingOrderId = null
            this.autoOpened = false
            this.playSound = false

            // Dirty hack to ensure the modal gets closed, and reopened. Which should correctly adjust all data show in modal
            setTimeout(() => this.checkNewOrders(), 700)
        },
        toCartOrder(order: Order): any {
            order = cloneDeep(order)
            const cartStore = useCartStore()
            // @todo, there is a customer in the order, why not using that one?
            const customer = Object.assign(
                getDefaultCustomer(),
                order.customer_id
                    ? {
                          id: order.customer_id,
                          address: order.address,
                          company: order.company,
                          first_name: order.first_name,
                          last_name: order.last_name,
                          billing_address: order.billing_address,
                          phone: order.phone,
                          email: order.email,
                          invoice: {
                              reference: { value: order.source.id },
                              enabled: order.customer?.invoice?.enabled,
                          },
                          notes: order.customer?.notes || null,
                      }
                    : {}
            )

            if (
                (order.packaging_costs ?? 0) > 0 &&
                order.has_custom_packaging_costs
            ) {
                usePOSStore().packaging_costs = order.packaging_costs ?? 0
            }

            return {
                currentCart: {
                    customer_id: order.customer_id,
                    cart: {
                        order_id: order.id,
                        products: order.products.map(
                            (product: OrderProduct) => {
                                const discountId: RegExpMatchArray | null = (
                                    product.description || ""
                                ).match(/(dpp?\d+)$/)

                                return {
                                    id: product.product_id,
                                    order_product_id: product.id,
                                    title:
                                        product.product_id <= 0
                                            ? product.description ||
                                              product.product?.title?.counter ||
                                              product.product?.title?.default ||
                                              null
                                            : null,
                                    price: product.price_unit,
                                    quantity: product.quantity,
                                    items: product.extras,
                                    remarks: product.remarks,
                                    discount_id: discountId
                                        ? discountId[0]
                                        : null,
                                    total: product.total,
                                    discount: product.discount,
                                    deposit: product.deposit,
                                    is_free:
                                        discountId &&
                                        discountId[0].startsWith("dpp"),
                                }
                            }
                        ),
                        tip: order.tip,
                        type: order.delivery || cartStore.defaultDispatchType,
                        points: order.points.paid || 0,
                        zip_code: order.address.zipcode || null,
                        schedule: {
                            time: order.time === "asap" ? null : order.time_set,
                            date: DateTime.fromISO(order.created_at)
                                .toUTC()
                                .toFormat("yyyy-MM-dd"),
                            asap: order.time === "asap",
                        },
                        discounts: (order.discounts || []).map((discount) => {
                            let type = discount.type
                            if (
                                [
                                    DiscountType.SIMPLE.valueOf(),
                                    DiscountType.DISCOUNTED_PRODUCT.valueOf(),
                                    DiscountType.FREE_PRODUCT.valueOf(),
                                ].includes(discount.type)
                            ) {
                                type = "code"
                            }

                            if (
                                [
                                    DiscountType.CUSTOM.valueOf(),
                                    DiscountType.EXTERNAL.valueOf(),
                                ].includes(type)
                            ) {
                                return {
                                    type: type,
                                    // @ts-ignore
                                    code: "",
                                    custom: {
                                        type: discount.amount_type.valueOf(),
                                        description: discount.label,
                                        // @ts-ignore
                                        amount: discount.amount_type_value,
                                    } as CartDiscount,
                                }
                            }

                            return {
                                type: type,
                                is_external: false,
                                code: discount.code || "",
                            } as CartDiscount
                        }),
                        payment_method: order.payment.method,
                        payment2: {
                            method: order.payment2.method,
                            amount: order.payment2.amount,
                        },
                        payment_status: order.payment.status,
                        remarks: order.remarks,
                        products_can_be_updated: order.products_can_be_updated,
                    },
                    costs: {
                        payment: order.payment.costs,
                        delivery: order.delivery_costs,
                        packaging: 0,
                        deposit: order.deposit,
                        tip: order.tip,
                    },
                    choices: [],
                    discounts: order.discounts,
                    points: {
                        spent: order.points.paid,
                        earned: order.points.earned,
                    },
                    discount: order.discount.amount,
                    subtotal: order.subtotal,
                    total: order.total,
                    total_rounded: order.total_rounded,
                    cash_amount:
                        order.payment.method === PaymentMethod.CASH.valueOf()
                            ? parseFloat(order.payment.option)
                            : null,
                    table_id: order.table_id,
                    is_loaded_from_order: true,
                },
                customer: customer,
                table_id: order.table_id,
                existingOrderWasPaid: order.payment.status === "paid",
            }
        },
        async storeOrder(cartStore: CartState) {
            const masterSlave = useMasterSlave()

            return await axios.post(
                "/client/orders?fields[orders]=id,client_id,nr,refnr,source,address,has_seen,is_new_customer,table_name,is_v3,payment,created_at,time,time_set,total,status,delivery,distance,duration,payment,payment2,vat_rates,paid_at,phone_code,discount,total_netto,total_rounded,language,table_id,delivery_costs,products_can_be_updated,remarks,tip&include=customer,products,table",
                Object.assign(cloneDeep(cartStore.currentCart), {
                    customer: {
                        id: cartStore.customer.id || null,
                        notes: cartStore.customer.notes,
                    },
                    address: cartStore.customer.address,
                    onlinePayment: {
                        linkVia: cartStore.onlinePaymentLinkVia,
                        email: cartStore.customer.email,
                        phone: cartStore.customer.phone,
                    },
                    is_slave: masterSlave.isSlave.value,
                    device_id: masterSlave.obId,
                    cart: {
                        ...cloneDeep(cartStore.currentCart.cart),
                        payment_method:
                            cartStore.currentCart.cart.payment_method ===
                            PaymentMethod.PIN_CASH
                                ? PaymentMethod.CASH
                                : cartStore.currentCart.cart.payment_method,
                    },
                    invoice_reference:
                        cartStore.customer.invoice?.reference?.value,
                    source_info: useUserStore().waiterbuddy_platform,
                }),
                {
                    params: {
                        main_location_id: multiLocationStore().isActive
                            ? multiLocationStore().main_location_id
                            : 0,
                        multi_location_id: multiLocationStore().isActive
                            ? multiLocationStore().selected_location_id_for_menu
                            : 0,
                    },
                    paramsSerializer: paramsSerializer,
                }
            )
        },
        removeDuplicateOrders(): void {
            console.log("Checking for double orders")
            const seenIds: Set<number> = new Set<number>()

            this.orders = this.orders.filter(
                (order: UnwrapRef<Order>): boolean => {
                    if (seenIds.has(order.id)) {
                        console.warn(
                            `Duplicate order found and removed: ${order.id}`
                        )

                        return false // Remove duplicate
                    }
                    seenIds.add(order.id)

                    return true // Keep the first occurence of an order
                }
            )
        },
        startDuplicateOrderCleanup(): void {
            if (this.cleanupInterval) {
                clearInterval(this.cleanupInterval) // Prevent double intervals
            }

            this.cleanupInterval = setInterval((): void => {
                this.removeDuplicateOrders()
            }, 5000) as unknown as number // Milliseconds
        },
        stopDuplicateOrderCleanup(): void {
            console.log("Stop checking for double orders")
            if (this.cleanupInterval) {
                clearInterval(this.cleanupInterval)
                this.cleanupInterval = null
            }
        },
    },
})
